import React from 'react';
import { motion } from 'motion/react';
import { Shield, ArrowLeft, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/src/components/SEO';

export default function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden px-6">
      <SEO 
        title="404 – Page Not Found | AGRANDINTL"
        description="The page you are looking for does not exist on the AGRANDINTL website. Return to our homepage to explore our cybersecurity consulting services."
        keywords="404, page not found, AGRANDINTL"
      />
      
      {/* Background Accents */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-4xl max-h-[600px] opacity-20">
        <div className="absolute inset-0 bg-accent/10 blur-[120px] rounded-full animate-pulse" />
      </div>

      <div className="container max-w-2xl relative z-10 text-center space-y-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-accent/5 border border-accent/10 mb-8"
        >
          <Shield className="w-12 h-12 text-accent" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="space-y-4"
        >
          <h1 className="text-6xl md:text-8xl font-display font-black tracking-tighter text-foreground leading-none">
            404
          </h1>
          <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-accent">
            Secure Connection Lost
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto leading-relaxed font-normal">
            The page you requested could not be located. It may have been moved, deleted, or you might have followed an outdated link.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button asChild size="lg" className="h-14 px-8 rounded-xl font-bold group">
            <Link to="/">
              <Home className="mr-2 w-4 h-4 transition-transform group-hover:-translate-y-1" />
              Return to Base
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-14 px-8 rounded-xl border-white/5 hover:bg-white/5 font-bold group">
            <button onClick={() => window.history.back()}>
              <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
              Go Back
            </button>
          </Button>
        </motion.div>

        <div className="pt-12 border-t border-white/5 flex flex-col items-center gap-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground/40">
            Resource Verification Status: Failed
          </p>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="w-8 h-1 bg-white/5 rounded-full overflow-hidden">
                <div className="w-full h-full bg-accent/20 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
